import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Input,
  InputLabel,
  InputRoot,
} from '@mint-lib/tw-components';
import { TextArea } from '@mint-lib/tw-components/patterns';

import { useFolderAccessDraft } from '../../../hooks/knowledge-base/useFolderAccessDraft.js';
import { useFolderAccessUsers } from '../../../hooks/knowledge-base/useFolderAccessUsers.js';
import { useFolderMutations } from '../../../hooks/knowledge-base/useFolderMutations.js';
import { FolderAccessFields } from './FolderAccessFields.jsx';

const NAME_MAX_LENGTH = 200;

type Props = {
  validateName: (name: string) => boolean;
  onClose: () => void;
};

const CreateSharedFolderDialog = ({ validateName, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { createSharedFolder, isPending: isCreating } = useFolderMutations();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const users = useFolderAccessUsers();
  const draft = useFolderAccessDraft({ meId: users.me.id });

  const canCreate = validateName(name) && !isCreating;

  const handleCreate = async () => {
    if (!canCreate) return;

    const isCreated = await createSharedFolder({
      name,
      description,
      grants: draft.toRequest(),
    });

    if (isCreated) onClose();
  };

  return (
    <Dialog modal open onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot aria-describedby={undefined} size="s" className="w-130">
          <DialogHeader>
            <DialogTitle>{t('New shared folder')}</DialogTitle>
          </DialogHeader>

          <DialogContent className="flex flex-col gap-6">
            <InputRoot>
              <InputLabel
                htmlFor="shared-folder-name"
                label={<>{t('Folder name')}*</>}
              />
              <Input
                id="shared-folder-name"
                size="medium"
                placeholder={t('New folder')}
                maxLength={NAME_MAX_LENGTH}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </InputRoot>

            <TextArea
              id="shared-folder-description"
              label={t('Description')}
              placeholder={t('New folder')}
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />

            <FolderAccessFields draft={draft} users={users} />
          </DialogContent>

          <DialogFooter className="flex flex-row items-center justify-end gap-2">
            <Button variant="plain" size="large" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              disabled={!canCreate}
              data-dd-action-name="Knowledge base: create shared folder confirm"
              onClick={handleCreate}
            >
              {t('Create folder')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { CreateSharedFolderDialog };
